import { useEffect, useState } from 'react';

import { Accordion, Badge, Spinner } from 'react-bootstrap';

import { humanLabel } from '../helpers/humanLabel';

const CATEGORY_VARIANTS = {
    'Authentication': 'primary',
    'Cleanup': 'dark',
    'Data Entry': 'warning',
    'Navigation': 'info',
    'Setup': 'secondary',
    'Validation': 'success'
};

const STATUS_GLYPH = {
    completed: { color: '#16a34a', icon: '✓' },
    executing: { color: '#3b82f6', icon: '◐' },
    failed: { color: '#dc2626', icon: '✗' },
    note: { color: '#888', icon: '·' },
    proposed: { color: '#f59e0b', icon: '○' },
    skipped: { color: '#888', icon: '↷' }
};

const TERMINAL_SUCCESS = ['completed', 'passed'];
const TERMINAL_ALL = [...TERMINAL_SUCCESS, 'failed', 'cancelled', 'disconnected', 'ws_error'];

function ToolCallRow({ showDetails, tc }) {

    const glyph = STATUS_GLYPH[tc.status] || STATUS_GLYPH.proposed;
    const label = humanLabel(tc.toolName, tc.toolInput, tc.reasoning);

    // Suppress meta tools entirely from the friendly view (humanLabel
    // returns null for them) so the sidebar isn't cluttered with
    // set_progress noise. Still shown when details are toggled on.
    if (label == null && !showDetails) return null;

    if (showDetails) {

        return (
            <li className="d-flex gap-2 align-items-baseline" style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>
                <span style={{ color: glyph.color, fontWeight: 700 }}>
                    {glyph.icon}
                </span>
                <span style={{ fontWeight: 600 }}>
                    {tc.toolName}
                </span>
                <span className="text-muted text-truncate flex-grow-1" title={JSON.stringify(tc.toolInput)}>
                    {tc.toolInput ? JSON.stringify(tc.toolInput) : ''}
                </span>
            </li>
        );

    }

    return (
        <li className="d-flex gap-2 align-items-baseline" style={{ fontSize: 12 }}>
            <span style={{ color: glyph.color, fontWeight: 700 }}>
                {glyph.icon}
            </span>
            <span className="text-truncate flex-grow-1" title={`${tc.toolName} ${tc.toolInput ? JSON.stringify(tc.toolInput) : ''}`}>
                {label}
            </span>
        </li>
    );

}

function FlatList({ showDetails, steps }) {

    return (
        <ul className="list-unstyled small mb-0 d-flex flex-column gap-1">
            {steps.map(s => <ToolCallRow key={s.id} showDetails={showDetails} tc={s} />)}
        </ul>
    );

}

/**
 * Derive a phase-level status from the run's progress pointer + the
 * statuses of the tool calls that fired during the phase.
 */
function derivePhaseStatus(pi, currentPhaseIndex, phaseTools, status) {

    const runDone = TERMINAL_ALL.includes(status);
    const runSucceeded = TERMINAL_SUCCESS.includes(status);

    const hasFailed = phaseTools.some(t => t.status === 'failed');
    const hasSkipped = phaseTools.some(t => t.status === 'skipped');

    if (pi > currentPhaseIndex && !runDone) return 'not_started';
    if (pi === currentPhaseIndex && !runDone) return 'in_progress';
    if (hasFailed) return 'failed';
    if (hasSkipped) return 'has_skipped';
    if (pi <= currentPhaseIndex || runSucceeded) return 'success';

    return 'not_started';

}

function PhaseStatusIcon({ phaseStatus }) {

    switch (phaseStatus) {

        case 'in_progress':
            return <Spinner animation="border" size="sm" style={{ borderWidth: 2, height: 14, width: 14 }} variant="primary" />;

        case 'success':
            return (
                <span style={{ color: '#16a34a', fontSize: 14, fontWeight: 700, lineHeight: 1 }}>
                    ✓
                </span>
            );

        case 'failed':
            return (
                <span style={{ color: '#dc2626', fontSize: 14, fontWeight: 700, lineHeight: 1 }}>
                    ✗
                </span>
            );

        case 'has_skipped':
            return (
                <span style={{ color: '#f59e0b', fontSize: 14, fontWeight: 700, lineHeight: 1 }}>
                    ↷
                </span>
            );

        default:
            return null;

    }

}

export const StepLog = ({ currentPhaseIndex, currentStepIndex, phases, showDetails, status, steps }) => {

    // Controlled accordion state. Auto-opens the current phase as it advances;
    // user can manually expand or collapse any phase via clicks (Accordion's
    // onSelect updates openKeys directly).
    const [openKeys, setOpenKeys] = useState([]);

    useEffect(() => {

        if (currentPhaseIndex == null) return;
        setOpenKeys([String(currentPhaseIndex)]);

    }, [currentPhaseIndex]);

    if (!Array.isArray(phases) || phases.length === 0)
        return <FlatList showDetails={showDetails} steps={steps} />;

    const orphans = steps.filter(s => (
        s.phase_index == null
        || s.phase_index < 0
        || s.phase_index >= phases.length
    ));

    return (

        <div className="d-flex flex-column gap-2 overflow-auto flex-grow-1">

            <Accordion
                alwaysOpen
                activeKey={openKeys}
                onSelect={keys => setOpenKeys(keys || [])}
            >

                {phases.map((phase, pi) => {

                    const phaseTools = steps.filter(s => s.phase_index === pi);
                    const phaseStatus = derivePhaseStatus(pi, currentPhaseIndex, phaseTools, status);

                    return (

                        <Accordion.Item eventKey={String(pi)} key={pi}>

                            <Accordion.Header>
                                <div className="d-flex align-items-center gap-2 flex-grow-1 pe-2" style={{ minWidth: 0 }}>
                                    <Badge bg={CATEGORY_VARIANTS[phase.category] || 'secondary'} style={{ fontSize: 9 }}>
                                        {phase.category}
                                    </Badge>
                                    <span
                                        style={{
                                            fontSize: 13,
                                            opacity: phaseStatus === 'not_started' ? 0.6 : 1
                                        }}
                                        className="fw-semibold text-truncate"
                                    >
                                        {phase.label}
                                    </span>
                                    <span
                                        className="ms-auto d-flex align-items-center justify-content-end"
                                        style={{ minWidth: 18 }}
                                    >
                                        <PhaseStatusIcon phaseStatus={phaseStatus} />
                                    </span>
                                </div>
                            </Accordion.Header>

                            <Accordion.Body className="px-2 py-2">

                                <ol className="ps-3 mb-0" style={{ fontSize: 12 }}>
                                    {(phase.steps || []).map((stepText, si) => {

                                        const stepTools = phaseTools.filter(s => s.step_index === si);
                                        const isCurrentStep =
                                            phaseStatus === 'in_progress'
                                            && si === currentStepIndex;

                                        return (

                                            <li
                                                className={isCurrentStep ? 'fw-bold' : ''}
                                                key={si}
                                                style={{ marginBottom: 4 }}
                                            >
                                                <span style={{ opacity: isCurrentStep ? 1 : 0.7 }}>
                                                    {stepText}
                                                </span>
                                                {stepTools.length > 0 && (
                                                    <ul className="list-unstyled mt-1 mb-2" style={{ paddingLeft: 8 }}>
                                                        {stepTools.map(tc => <ToolCallRow key={tc.id} showDetails={showDetails} tc={tc} />)}
                                                    </ul>
                                                )}
                                            </li>

                                        );

                                    })}
                                </ol>

                            </Accordion.Body>

                        </Accordion.Item>

                    );

                })}

            </Accordion>

            {orphans.length > 0 && (

                <div className="mt-1">
                    <div className="text-muted small mb-1" style={{ fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                        Other
                    </div>
                    <FlatList showDetails={showDetails} steps={orphans} />
                </div>

            )}

        </div>

    );

};
